import { useEffect, useMemo, useRef } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import { filterTokens, toDocument, fromDocument } from './tokenMarkers'

/**
 * Champ de texte où l'on INSÈRE UN CHAMP DU FORMULAIRE en tapant « @ ».
 *
 * POURQUOI. L'objet et le corps d'une notification de formulaire reprennent la
 * saisie du visiteur : le serveur y remplace des marqueurs `{{ clé }}`. La clé
 * est le nom INTERNE du champ — `firstname`, `pageUrl` —, qu'aucun éditeur n'a
 * de raison de connaître, et qu'il devait jusqu'ici deviner puis taper sans
 * faute. Ici il tape « @ », déroule les champs par leur INTITULÉ, et le champ
 * choisi s'affiche comme une pastille. Même geste et même mécanique que les
 * mentions des commentaires de la gestion de projet (TipTap `Mention`).
 *
 * CE QUI EST STOCKÉ NE CHANGE PAS : la valeur reste le texte à marqueurs
 * `{{ clé }}`, la seule chose que le serveur sache remplacer (`FormNotifier`,
 * appelé par l'action ECA `orbit_builder_send_form_notifications` comme par le
 * contrôleur CRM). La pastille n'est qu'un AFFICHAGE de ce marqueur : rien à
 * migrer, les messages déjà écrits se rouvrent en pastilles, et un marqueur
 * tapé à la main — ou visant un champ inconnu — reste du texte, donc reste
 * intact à l'enregistrement.
 *
 * Pourquoi du React et pas du YAML : la surface est une pop-in du builder (le
 * registre d'éditeurs, hors rjBuild), et rendre un fragment de texte comme une
 * pastille non éditable demande un éditeur de document — aucune déclaration
 * reactive-json ne le couvre.
 *
 * Props :
 *  - value        (string)  le texte à marqueurs
 *  - onChange     (string => void)
 *  - tokens       ([{key, label}]) les champs qu'« @ » déroule
 *  - id, label, placeholder, multiline, rows
 */

/**
 * La liste déroulante des champs — même dispositif que les mentions des
 * commentaires : une couche posée sur `<body>` (l'éditeur vit dans une pop-in
 * qui rogne son contenu), pilotée entièrement au clavier.
 */
const createTokenSuggestionRenderer = (openRef) => {
  let popup = null
  let currentProps = null
  let activeIndex = 0

  /**
   * Poser le champ choisi.
   *
   * TipTap recopie les propriétés de l'élément TELLES QUELLES en attributs du
   * nœud, et le nœud `mention` ne connaît que `id` et `label` : lui passer
   * notre `{key, label}` déposait une pastille SANS identifiant. Elle
   * s'affichait (le rendu lit `label`), mais s'enregistrait `{{  }}` — un
   * marqueur vide, que le serveur remplace par rien, et qui ne se rouvrait plus
   * en pastille. La clé devient l'`id` ici, et nulle part ailleurs.
   */
  const pick = (item) => currentProps?.command?.({ id: item.key, label: item.label })

  const destroy = () => {
    popup?.remove()
    popup = null
    if (openRef) openRef.current = null
    currentProps = null
    activeIndex = 0
  }

  const renderItems = () => {
    if (!popup || !currentProps) return
    const { items, clientRect } = currentProps

    const rect = typeof clientRect === 'function' ? clientRect() : clientRect
    if (rect) {
      popup.style.left = `${rect.left}px`
      popup.style.top = `${rect.bottom + 4}px`
    }

    popup.replaceChildren()
    const list = document.createElement('div')
    list.className = 'builder-token-menu'

    if (!items || items.length === 0) {
      const empty = document.createElement('p')
      empty.className = 'builder-token-empty'
      empty.textContent = 'Aucun champ ne correspond'
      list.appendChild(empty)
      popup.appendChild(list)
      return
    }

    // Un vrai bouton par champ : la liste se parcourt aussi à la souris, et le
    // nom accessible est le texte visible.
    items.forEach((item, i) => {
      const option = document.createElement('button')
      option.type = 'button'
      option.className = 'builder-token-option'
      option.dataset.active = String(i === activeIndex)
      option.textContent = item.label
      // `mousedown` et non `click` : le clic ferait d'abord perdre le focus à
      // l'éditeur, donc la position d'insertion.
      option.addEventListener('mousedown', (e) => {
        e.preventDefault()
        e.stopPropagation()
        pick(item)
      })
      list.appendChild(option)
    })
    popup.appendChild(list)
  }

  return {
    onStart(props) {
      currentProps = props
      activeIndex = 0
      popup = document.createElement('div')
      popup.className = 'builder-token-popup'
      // La couche est posée sur <body> : elle se déclare comme appartenant à
      // l'éditeur, sinon la pop-in du builder prend le clic pour un clic DEHORS
      // et se referme avant que le champ ne soit inséré.
      popup.dataset.editorLayer = ''
      document.body.appendChild(popup)
      // Le champ doit savoir que le menu est ouvert : c'est ce qui lui fait
      // rendre Entrée et Échap au menu plutôt que de les traiter lui-même.
      if (openRef) openRef.current = destroy
      renderItems()
    },
    onUpdate(props) {
      currentProps = props
      activeIndex = 0
      renderItems()
    },
    onKeyDown({ event }) {
      if (!popup || !currentProps) return false
      const items = currentProps.items || []
      if (event.key === 'Escape') {
        // La liste se ferme AVANT que la pop-in du builder ne voie l'échappement :
        // sinon un « @ » malheureux fermerait tout l'éditeur.
        destroy()
        return true
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (items.length === 0) return true
        event.preventDefault()
        const delta = event.key === 'ArrowDown' ? 1 : -1
        activeIndex = Math.min(Math.max(activeIndex + delta, 0), items.length - 1)
        renderItems()
        return true
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        if (items.length === 0 || !items[activeIndex]) return false
        event.preventDefault()
        pick(items[activeIndex])
        return true
      }
      return false
    },
    onExit() {
      destroy()
    },
  }
}

export const TokenTextField = ({
  id,
  value,
  onChange,
  tokens,
  label,
  placeholder,
  multiline = false,
  rows = 4,
}) => {
  // Les champs disponibles changent quand l'éditeur renomme un intitulé : la
  // liste est lue au moment où « @ » ouvre le menu, jamais figée à la création
  // de l'éditeur (que TipTap ne recrée pas).
  const tokensRef = useRef(tokens)
  tokensRef.current = Array.isArray(tokens) ? tokens : []

  // La valeur est PILOTÉE par le parent, mais chaque frappe la renvoie : sans
  // ce garde-fou, le document serait réécrit sous le curseur à chaque lettre.
  const selfUpdate = useRef(false)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Le menu ouvert, et de quoi le fermer. Deux touches lui appartiennent tant
  // qu'il est là — Entrée (choisir) et Échap (renoncer) —, et il faut les lui
  // rendre : elles sont sinon interceptées AVANT lui, plus haut (voir plus bas).
  const menuRef = useRef(null)

  const extensions = useMemo(() => [
    // Un texte de courrier : ni titre, ni liste, ni gras — le corps part en
    // texte brut, tout enrichissement mentirait sur le résultat.
    StarterKit.configure({
      heading: false,
      bold: false,
      italic: false,
      strike: false,
      code: false,
      codeBlock: false,
      blockquote: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      horizontalRule: false,
      link: false,
    }),
    Mention.configure({
      HTMLAttributes: { class: 'builder-token' },
      // Copié, exporté, relu : un marqueur, jamais « @Prénom ».
      renderText: ({ node }) => `{{ ${node.attrs.id} }}`,
      renderHTML: ({ node }) => ['span', { class: 'builder-token' }, node.attrs.label ?? node.attrs.id],
      suggestion: {
        char: '@',
        allowSpaces: false,
        // « @ » N'OUVRE LE MENU QU'EN DÉBUT DE JETON. Sans cela, la colonne
        // « destinataires » serait inutilisable : taper `contact@exemple.fr`
        // déroulerait les champs au milieu de l'adresse. TipTap ne déclenche la
        // suggestion que si le caractère est en tête de bloc ou précédé d'un
        // des `allowedPrefixes` (défaut : l'espace seul) — on y ajoute la
        // virgule et le point-virgule, les deux séparateurs d'une liste
        // d'adresses, pour que `a@b.fr,@` déroule quand même.
        allowedPrefixes: [' ', ',', ';'],
        items: ({ query }) => filterTokens(tokensRef.current, query),
        render: () => createTokenSuggestionRenderer(menuRef),
      },
    }),
    Placeholder.configure({ placeholder: placeholder || '' }),
  ], [placeholder])

  const editor = useEditor({
    extensions,
    content: toDocument(value, tokensRef.current),
    editorProps: {
      attributes: {
        // L'élément éditable porte l'identifiant et le nom accessible : un
        // `<label for>` ne peut pas étiqueter un conteneur `contenteditable`.
        ...(id ? { id } : {}),
        role: 'textbox',
        'aria-multiline': multiline ? 'true' : 'false',
        ...(label ? { 'aria-label': label } : {}),
        class: 'builder-tokenfield-input',
      },
      // Un objet de courrier tient sur UNE ligne : Entrée y validerait un
      // paragraphe invisible dans le message envoyé. MAIS pas quand le menu des
      // champs est ouvert : Entrée y choisit le champ. ProseMirror consulte ces
      // props AVANT les greffons — sans cette réserve, l'objet n'accepterait
      // jamais un champ au clavier (constaté à l'écran, sans erreur).
      handleKeyDown: multiline
        ? undefined
        : (_view, event) => event.key === 'Enter' && !menuRef.current,
    },
    onUpdate: ({ editor: ed }) => {
      selfUpdate.current = true
      onChangeRef.current?.(fromDocument(ed.getJSON()))
    },
  })

  // ÉCHAP APPARTIENT AU MENU tant qu'il est déroulé.
  //
  // La pop-in du builder écoute l'échappement sur `document`, EN CAPTURE : sans
  // cela, renoncer à un « @ » malheureux fermerait tout l'éditeur et perdrait le
  // message en cours d'écriture. On se pose donc sur le même nœud, avant elle
  // (les effets d'un enfant s'exécutent avant ceux du parent), et on coupe la
  // propagation à l'IMMÉDIAT — deux écouteurs sur le même nœud, seul
  // `stopImmediatePropagation` retient le second.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || !menuRef.current) return
      e.stopImmediatePropagation()
      e.preventDefault()
      menuRef.current()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [])

  // Valeur poussée de l'extérieur (changement de ligne dans la liste, réamorce)
  // : on ne réécrit le document que si elle diffère de ce qu'il porte déjà.
  useEffect(() => {
    if (!editor) return
    if (selfUpdate.current) {
      selfUpdate.current = false
      return
    }
    const current = fromDocument(editor.getJSON())
    if (current !== (value ?? '')) {
      editor.commands.setContent(toDocument(value, tokensRef.current), { emitUpdate: false })
    }
  }, [editor, value])

  if (!editor) return null

  return (
    <div
      className={`builder-tokenfield${multiline ? ' builder-tokenfield-multi' : ''}`}
      style={multiline ? { minHeight: `${rows * 1.5 + 1.25}rem` } : undefined}
      onMouseDown={(e) => {
        // Cliquer la marge du cadre entre dans le champ, comme dans un <textarea>.
        if (e.target === e.currentTarget) editor.commands.focus('end')
      }}
    >
      <EditorContent editor={editor} />
    </div>
  )
}
